import React, {Fragment, ReactNode, useEffect, useState} from 'react';
import {useAppSelector} from 'app/store';
import {LogoHeader} from 'components/RatingModal/LogoHeader';
// import DeelOnboardingLink from 'features/Provider/DigitalPractice/Revenue/DeelOnboardingLink';
import PaypalOnboardingLink from 'features/Provider/DigitalPractice/Revenue/PaypalOnboardingLink';
import StripeOnboardingLink from 'features/Provider/DigitalPractice/Revenue/StripeOnboardingLink';
import {selectProviderFeePercentage} from 'features/Provider/providerSelectors';
import {selectUserProfile} from 'features/User';
import {TherapistProfile} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {getCountryDetailsByIsoCode, isMobile} from 'utils';

import PaystackOnboardingLink from '../../DigitalPractice/Revenue/PaystackOnboardingLink';

import {PaymentCard} from './Components/PaymentCard';
import {PaymentSettingsDrawer} from './PaymentSettingsDrawer';
import {PaymentSettingsModal} from './PaymentSettingsModal';

const MAX = 20;
const MIN = 0;
export function PaymentSettings(): React.ReactElement {
  const {t} = useTranslation();
  const user = useAppSelector(selectUserProfile) as TherapistProfile;

  const [paymentType, setPaymentType] = useState<
    'platform_fee' | 'session_rate'
  >();
  const [showModal, setShowModal] = useState<boolean>(false);

  // const isUserFromUS = isUserFromUnitedStates(user);

  let onboardingLinks: ReactNode[];
  if (user?.defaultPaymentService === 'stripe') {
    onboardingLinks = [<StripeOnboardingLink key="stripe" />];
  } else if (user?.defaultPaymentService === 'paypal') {
    onboardingLinks = [<PaypalOnboardingLink key="paypal" />];
  } else if (user?.defaultPaymentService === 'paystack') {
    onboardingLinks = [<PaystackOnboardingLink key="paystack" />];
  } else {
    onboardingLinks = [];
  }

  const userPercentage = useAppSelector(selectProviderFeePercentage) ?? 20;

  const isCustomFeePercentage =
    userPercentage && ![MAX, MIN, 10].includes(userPercentage);

  useEffect(() => {
    !showModal && setPaymentType(undefined);
  }, [showModal]);

  const currencySymbol =
    getCountryDetailsByIsoCode(user.countryOfResidence.code)?.currencySymbol ||
    '$';
  const sessionRate = `${currencySymbol} ${
    user.sessionRate?.digitalPractice?.min || 0
  } - ${user.sessionRate?.digitalPractice?.max || 0}`;

  useEffect(() => {
    setShowModal(false);
    setPaymentType(undefined);
  }, [userPercentage, sessionRate]);

  const hasProviderLikedAnyPaymentService =
    user?.paypalMerchantId?.length ||
    (user?.stripeAccountId?.length && user?.stripeConnectOnboardingComplete) ||
    user?.paystackSubaccountDetails?.subaccountId;

  return (
    <>
      <div className="flex flex-col py-3">
        <h2 className="flex items-center gap-4 font-inter text-2xl leading-7 font-medium mb-10">
          <LogoHeader classes="pt-0" />{' '}
          {t('providerProfileMenuItems.revenue.dp.priceAndFee')}
        </h2>

        {!hasProviderLikedAnyPaymentService ? (
          <h3 className="font-semibold text-lg pb-3  font-inter">
            Sign up to see your Revenue
          </h3>
        ) : null}

        <div className="flex flex-col gap-5">
          {hasProviderLikedAnyPaymentService ? (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PaymentCard
                title={t(
                  'providerProfileMenuItems.revenue.dp.platformFee.title',
                )}
                rate={`${userPercentage > 0 ? userPercentage.toFixed(2) : 0}%`}
                rateType={isCustomFeePercentage ? 'Custom split' : ''}
                onClick={() => {
                  setShowModal(true);
                  setPaymentType('platform_fee');
                }}
              />

              <PaymentCard
                title={t(
                  'providerProfileMenuItems.revenue.dp.sessionRate.title',
                )}
                rate={sessionRate}
                onClick={() => {
                  setShowModal(true);
                  setPaymentType('session_rate');
                }}
              />
            </section>
          ) : null}

          <section className="flex flex-col gap-4">
            {onboardingLinks.map((link, index) => (
              <Fragment key={`payment-link-${index}`}>{link}</Fragment>
            ))}
          </section>

          {/* {isUserFromUS ? (
            <section className="flex flex-col gap-4">
              <DeelOnboardingLink />
            </section>
          ) : null} */}
        </div>
      </div>

      {/* Modals */}
      {paymentType && !isMobile ? (
        <PaymentSettingsModal
          showModal={showModal}
          paymentType={paymentType}
          closeModal={() => setShowModal(false)}
        />
      ) : null}

      {paymentType && isMobile ? (
        <PaymentSettingsDrawer
          showModal={showModal}
          paymentType={paymentType}
          closeModal={() => setShowModal(false)}
        />
      ) : null}
    </>
  );
}
